import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  Output,
  QueryList,
  signal,
  Signal,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { CompanyPageSectionEnums } from '../../classes/company';
import { baseUrl, publicMethods } from '../../globals';
import { CompanyService } from '../../services/company.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { combineLatest } from 'rxjs';
import { CommunicationService } from '../../services/communication.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IFeaturePermissions,
  ProfileOpenSearchType,
  MISortString,
} from '../../classes/types';
import { PropensityComponentOpacityPipe } from '../../pipes/propensity-component-opacity';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MessageService } from '../../services/message.service';
import { MatAccordion, MatExpansionPanel } from '@angular/material/expansion';
import { Location } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { ZintGrowSubscriptionsService } from '../../services/zint-grow-subscriptions.service';
import { DataSharingService } from '../../services/data-sharing.service';
import { TaggingService } from '../../services/tagging.service';
import { DonutDataInput } from '../html-snippet-components/donut-chart/donut-chart.component';
import { CRMIntegrationsService } from '../../services/crm-integrations.service';
import { AuthenticationService } from '../../services/authentication.service';
import { GetInsightsComponent } from '../html-snippet-components/get-insights/get-insights.component';
import { tap, switchMap, takeUntil, finalize } from 'rxjs/operators';
import { timer, Subscription, Subject, EMPTY, NEVER, BehaviorSubject, concat, Observable } from 'rxjs';
declare var $: any;
declare var zE: any; // Zendesk SDK

//Add permission only endpoints labels here
enum EndpointNames {
  ProfileData = 'profileData',
  SupplierData = 'supplierData',
  AIRAData = 'airaData',
  ShareholdersInData = 'shareholdersInData',
  ShareholdersOfData = 'shareholdersOfData',
  LandOwnershipData = 'landOwnershipData',
}

type LandRegistryEpcCertificate = {
  building_reference_number?: string | null;
  certificate_identifier?: string | null;
  certificate_number?: string | null;
  certificate_source?: 'legacy' | 'mhclg' | string | null;
  legacy_lmk_key?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  address_line_3?: string | null;
  address?: string | null;
  postcode?: string | null;
  posttown?: string | null;
  local_authority?: string | null;
  local_authority_label?: string | null;
  constituency?: string | null;
  constituency_label?: string | null;
  county?: string | null;
  uprn?: string | null;
  uprn_source?: string | null;
  lmk_key?: string | null;
  asset_rating?: number | null;
  asset_rating_band?: string | null;
  property_type?: string | null;
  floor_area?: number | null;
  primary_energy_value?: number | null;
  transaction_type?: string | null;
  building_level?: string | null;
  lodgement_datetime?: string | Date | null;
  lodgement_date?: string | Date | null;
  inspection_date?: string | Date | null;
  ac_inspection_commissioned?: string | null;
  main_heating_fuel?: string | null;
  building_environment?: string | null;
  aircon_present?: string | null;
  aircon_kw_rating?: number | null;
  estimated_aircon_kw_rating?: number | null;
  report_type?: string | null;
  standard_emissions?: number | null;
  target_emissions?: number | null;
  typical_emissions?: number | null;
  building_emissions?: number | null;
  new_build_benchmark?: number | null;
  existing_stock_benchmark?: number | null;
};

// Single-company answers normally complete in a few seconds. Poll quickly at
// first, then taper so a delayed queue does not cause sustained request load.
const MAGIC_INSIGHTS_POLL_DELAYS_MS = [
  5_000,
  5_000,
  5_000,
  10_000,
  15_000,
  30_000,
  60_000,
  120_000,
  180_000,
  180_000,
];

type LandRegistryAddressPayload = {
  title_number: string;
  tenure?: string | null;
  classification?: string | null;
  price_paid?: number | null;
  date_proprietor_added?: string | Date | null;
  address?: Partial<Record<string, string | null>>;
  epc_data?: LandRegistryEpcCertificate[];
} & Record<string, any>;
@Component({
  selector: 'company-profile-page',
  templateUrl: './company-profile-page.component.html',
  styleUrl: './company-profile-page.component.css',
  providers: [PropensityComponentOpacityPipe],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CompanyProfilePageComponent {
  canUseLogisticsProfile = false;
  canUseTaxAccountancyProfile = false;
  canUseFinancialServicesProfile = false;
  openFinancialServicesProfile(): void {
    if (!this.canUseFinancialServicesProfile) return;
    this.router.navigate(['/profile', this.companyNumber, 'financial-services'], {
      queryParams: this.route.snapshot.queryParams,
      fragment: this.route.snapshot.fragment,
    });
  }
  openTaxAccountancyProfile(): void {
    if (!this.canUseTaxAccountancyProfile) return;
    this.router.navigate(['/profile', this.companyNumber, 'tax-accountancy'], {
      queryParams: this.route.snapshot.queryParams,
      fragment: this.route.snapshot.fragment,
    });
  }
  switchProfileMode(mode: 'full' | 'logistics'): void {
    this.authenticationService.setDefaultSkin(mode).subscribe({
      next: () => this.router.navigate(
        mode === 'logistics'
          ? ['/profile', this.companyNumber, 'logistics']
          : ['/profile', this.companyNumber],
        {
          queryParams: this.route.snapshot.queryParams,
          fragment: this.route.snapshot.fragment,
        }
      ),
      error: () => this.pubMethods.showInfoMessage('Could not save your profile preference. Please try again.'),
    });
  }

  loadingStatus: Record<string, any> = {};
  loadingError: string = '';
  isSmallScreen: Signal<boolean> = signal(false);
  company: Record<string, any> = {};
  companyNumber: string = '';
  userCustomProperties: Record<string, any> = {};
  accountDetails: Partial<{
    isSuperUser: boolean;
    email: string;
    userId: number;
  }> = {};
  teamMembers: Record<string, any>[] = [];
  featurePermissions: Partial<IFeaturePermissions> = {};
  permissionsCheckMap: Partial<Record<CompanyPageSectionEnums, boolean>> = {};
  toggleAllNavSections: boolean = false;
  allNavSections: string[] = [];
  navSectionsInUserOrder: string[] = [];
  navSectionPolledCount: Partial<
    Record<CompanyPageSectionEnums, number | string>
  > = {};
  isFocusView: boolean = false;
  sectionInView: string = 'company_overview_section';
  isReordering: boolean = false;
  tempReOrderingList: any[];
  isAllExpansionPanelsOpen: boolean = false;
  expandedSections: Record<string, boolean> = {};
  zintGrowColumns: Record<string, any>[] = [];
  zintGrowColumnsFollowing: Record<string, any>[] = [];
  hasColumnEditAccess: boolean = false;

  availableCRMList: Record<string, any>[] = [];
  crmExporterName: string = '';

  fullWebsiteSearchTerm: string = '';
  accountsSearchText: string = '';
  accountsDateSelected: string = 'any';
  yearAccountsMadeUpToDates: string[] = [];

  fullWebsiteResults: Record<string, any>[] = [];
  accountsResults: Record<string, any> = {};
  customAttrsUpdated: boolean = false;

  companyAIPitch: string = '';

  selfPublishedCompanyNewsSearchText: string = '';
  thirdPartyNewsSearchText: string = '';

  openSearchModalTitle: string = '';
  currentOpenSearchType: string = '';

  availableAssetsData: Record<string, any> = {};
  sparkLineRowData: Record<string, any> = {};

  jobsListToRender: Record<string, any>[] = [];
  jobSearchTerm: string = '';

  impExpModalType: 'imports' | 'exports' = 'imports';

  companyShareholdersOf: Record<string, any>[] = [];
  companiesShareholderIn: Record<string, any>[] = [];
  confirmedShareholdersData: Partial<DonutDataInput>[] = [];
  totalNumberOfShares: number = 0;
  confirmationStatementsTabIndex: number = 0;

  renderStructureGraphs: boolean = false;
  showClusterNotStructure: boolean = false;

  impExpChartColorScheme = {
    imports: {
      domain: ['#FBFBFB', 'skyblue', '#00A1E0'],
    },
    exports: {
      domain: ['#FBFBFB', '#D2C2E4', '#9D7AC4'],
    },
  };

  detailedImportsData: Record<string, any>[] = [];
  detailedExportsData: Record<string, any>[] = [];

  governmentContractsToRender: Record<string, any>[] = [];
  allGovernmentContracts: Record<string, any>[] = [];

  availableEnergyTerms: string[] = [];
  availableEmissionsTerms: string[] = [];

  supplierData: Record<string, any>[] = [];

  officersPSCsData: Record<string, any>[] = [];

  companyBanker = '';
  companyAccountant = '';
  companyAuditor = '';
  companyLawyer = '';

  airaData: Record<string, any>[] = [];
  airaSpecificNavItems: string[] = [];
  requestUnlockMagic = false;
  isRefreshingAiraInBackground = false;
  magicInsightsSortString: MISortString = null;

  activeMagicInsightsModuleId: number = null;
  magicInsightsModuleNamesIds: Record<string, any>[] = [];

  // Track Magic Insights modules currently being generated
  pendingModuleGenerations: Map<number, {
    airaRunId: string;
    pollSubscription: Subscription;
    attemptCount: number;
  }> = new Map();
  private pendingStatusChecks = new Set<number>();

  private destroy$ = new Subject<void>();
  private hasSubscribedToRouteFragment = false;

  // Track page visibility for polling optimization
  private pageVisible$ = new BehaviorSubject<boolean>(!document.hidden);
  private onVisibilityChange = () => {
    this.pageVisible$.next(!document.hidden);

    // If page just became visible AND we have pending modules, poll immediately
    if (!document.hidden && this.pendingModuleGenerations.size > 0) {
      this.pollAllPendingModules();
    }
  };

  landOwnershipData: LandRegistryAddressPayload[] = [];
  expandedEpcCertificates: Record<string, boolean> = {};

  @ViewChildren(MatExpansionPanel, { read: ElementRef })
  panelElements: QueryList<ElementRef>;
  @ViewChildren(MatExpansionPanel)
  expansionPanels: QueryList<MatExpansionPanel>;
  @ViewChild(MatAccordion) accordion: MatAccordion;
  @ViewChild('newsContainer') newsContainerRef: ElementRef;
  @ViewChild('getInsights') getInsights: GetInsightsComponent;

  @Input() companyNumberFromParent: string = null;
  @Output() endpointsLoaded = new EventEmitter<boolean>();

  endpointsDataFinishedLoading = signal({
    [EndpointNames.ProfileData]: false,
  });

  allEndpointsLoaded = computed(() => {
    return Object.values(this.endpointsDataFinishedLoading()).every(
      flag => flag
    );
  });

  loadingSignal = this.companyService.isLoadingSignal;

  constructor(
    private route: ActivatedRoute,
    private snackbar: MatSnackBar,
    public pubMethods: publicMethods,
    private companyService: CompanyService,
    private communicationService: CommunicationService,
    private messageService: MessageService,
    private propensityOpacityPipe: PropensityComponentOpacityPipe,
    private zone: NgZone,
    private titleService: Title,
    private zintGrowSubscriptionService: ZintGrowSubscriptionsService,
    public dataStore: DataSharingService,
    private taggingService: TaggingService,
    private crmService: CRMIntegrationsService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private location: Location,
    private cdr: ChangeDetectorRef
  ) {
    this.loadingStatus = this.companyService.isLoading;

    effect(() => {
      if (this.dataStore.companyTagsUpdated() === true) {
        this.getCompanyUserDetails();
      }

      if (
        this.requestUnlockMagic === false &&
        this.allEndpointsLoaded() === true
      ) {
        this.setupUserPreferences();
        this.scrollToSectionAndExpand();
        this.endpointsLoaded.emit(this.allEndpointsLoaded());
      }

      if (this.dataStore.unlockMagicModuleId() !== null) {
        const magicModuleId = this.dataStore.unlockMagicModuleId();
        this.dataStore.unlockMagicModuleId.set(null);
        this.openGetInsights(magicModuleId);
      }
    });
  }

  ngOnInit() {
    this.companyNumber =
      this.companyNumberFromParent || this.route.snapshot.paramMap.get('id');
    if (!this.companyNumberFromParent) {
      this.authenticationService.getPlanFeaturesEnabled().subscribe({
        next: plan => {
          const skins = Array.isArray(plan?.available_dashboard_skins)
            ? plan.available_dashboard_skins : [];
          this.canUseLogisticsProfile = skins.includes('logistics');
          this.canUseTaxAccountancyProfile = skins.includes('tax_accountancy');
          this.canUseFinancialServicesProfile = skins.includes('financial_services');
        },
        error: () => {
          this.canUseLogisticsProfile = false;
          this.canUseTaxAccountancyProfile = false;
          this.canUseFinancialServicesProfile = false;
        },
      });
    }

    this.communicationService.getEmailMessage().subscribe(email => {
      this.accountDetails = { ...this.accountDetails, email };
    });
    this.communicationService.getIsSuperUserMessage().subscribe(check => {
      this.accountDetails = { ...this.accountDetails, isSuperUser: check };
    });
    this.communicationService.getUserAccountId().subscribe(userId => {
      this.accountDetails = { ...this.accountDetails, userId };
    });
    this.checkPermissions();
    this.getAllNavSections();
    this.getCompanyProfilePageData();

    // Listen for page visibility changes to optimize polling
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  ngAfterViewInit() {
    this.checkIfSmallScreen();

    this.route.fragment.subscribe(fragment => {
      if (fragment === 'see-all-navs') {
        this.toggleAllNavSections = true;
        this.router.navigate([], {
          fragment: null,
        });
      }
    });
  }

  loadTwitterScript() {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://platform.twitter.com/widgets.js';
    document.body.appendChild(script);
  }

  checkIfSmallScreen(): void {
    this.isSmallScreen = this.pubMethods.isScreenSmallerThanBreakpoint(992);
  }

  toggleAccordion(): void {
    if (this.isAllExpansionPanelsOpen === false) {
      this.isAllExpansionPanelsOpen = true;
      localStorage.setItem(
        'profileExpandAll',
        JSON.stringify(this.isAllExpansionPanelsOpen)
      );
      this.accordion?.openAll();
      this.snackbar.open(
        "Preference setting saved. Clicking on 'Collapse All' will remove this setting.",
        'Okay',
        {
          duration: 6000,
        }
      );
    } else {
      this.isAllExpansionPanelsOpen = false;
      localStorage.removeItem('profileExpandAll');
      this.accordion.closeAll();
    }
  }

  setupUserPreferences(): void {
    const focusViewLS = JSON.parse(localStorage.getItem('profileFocusView'));

    if (focusViewLS) {
      this.isFocusView = true;
    }

    const expandAllLS = JSON.parse(localStorage.getItem('profileExpandAll'));
        
    if (expandAllLS) {
      this.isAllExpansionPanelsOpen = true;

      setTimeout(() => {
        // needs this delay for accordions to load async
        this.accordion?.openAll();
      }, 100);
    }

    const magicInsightsSortByLS = localStorage.getItem(
      'magicInsightsSortBy'
    ) as MISortString; // no need to parse as this is string

    if (magicInsightsSortByLS) {
      this.magicInsightsSortString = magicInsightsSortByLS;
    }
  }

  findExpansionPanelById(sectionId: string): MatExpansionPanel {
    /* find index of the panel from the native elements since we only have direct access to html id from ElementRef
    Then using this index, return the right MatExpansionPanel.
    */
    const panelIndex = this.panelElements
      .toArray()
      .findIndex(el => el.nativeElement.getAttribute('id') === sectionId);

    if (panelIndex > -1) {
      return this.expansionPanels.toArray()[panelIndex];
    }
  }

  setSectionExpandedState(sectionId?: string): void {
    const panel = this.findExpansionPanelById(sectionId);
    this.expandedSections = {
      ...this.expandedSections,
      [sectionId]: panel?.expanded,
    };
  }

  scrollToSectionAndExpand(sectionId?: string): void {
    if (sectionId) {
      this.zone.runOutsideAngular(() => {
        if (!this.companyNumberFromParent) {
          /**
           * router.navigate causes some unwanted scroll,
           * so replaceState is better here
           */
          this.location.replaceState(
            this.router.url.split('#')[0] + `#${sectionId}`
          );
        }
        this.sectionInView = sectionId;
        this.performScrollAndExpand(sectionId);
      });
    } else {
      /* Subscribe to route.fragment if sectionId is not provided
       * so it keeps track of the anchor fragments to scroll
       */
      // The constructor effect can call this more than once. Each extra subscription
      // replayed the current fragment and scrolled the page back to it, dragging the
      // reader off whichever section they had moved to.
      if (this.hasSubscribedToRouteFragment) return;
      this.hasSubscribedToRouteFragment = true;

      this.route.fragment
        .pipe(takeUntil(this.destroy$))
        .subscribe(fragment => {
          if (fragment) {
            this.zone.runOutsideAngular(() => {
              this.performScrollAndExpand(fragment);
            });
            this.sectionInView = fragment;
          }
        });
    }
  }

  private performScrollAndExpand(section: string): void {
    // just an abstraction to use within scrollToSection&Expand
    let frameAnimationRef: number;
    const navBarHeightOffset = 48; //navbar height to offset
    const isProfileInDrawer = !!this.companyNumberFromParent;

    frameAnimationRef = requestAnimationFrame(() => {
      if (!this.isFocusView) {
        this.companyService.scrollToSelectorOnceExists(
          section,
          navBarHeightOffset,
          isProfileInDrawer
        );
      }

      const sectionPanel = this.findExpansionPanelById(section);

      if (sectionPanel) {
        this.zone.run(() => {
          sectionPanel.expanded = true;
        });
        cancelAnimationFrame(frameAnimationRef);
      }
    });
  }

  checkPermissions(): void {
    const permissionObs$ = combineLatest([
      this.communicationService.getHasAIAccountsSummaryAccess(),
      this.communicationService.getHasAIPitchBuilderAccessMessage(),
      this.communicationService.getHasCrossSellAccessMessage(),
      this.communicationService.getHasSupplierDataAccessMessage(),
      this.communicationService.getHasPatentsAccessMessage(),
      this.communicationService.getHasEnergyAndEmissionsAccessMessage(),
      this.communicationService.getHasZintGrowAccessMessage(),
      this.communicationService.getHubspotAccess(),
      this.communicationService.getHasShareFilingsAccessMessage(),
      this.communicationService.getHasAIRAAccess(),
      this.communicationService.getHasPAFAccess(),
      // Add more permissions as needed
    ]);

    /**
     * destructuring observable array here but we can also use the old
     * subscribe(data => {
     * hasAiAccountsSummaryAccess: data[0],
     * hasAiPitchBuilderAccess: data[1], ...}).
     * Either approach needs to maintain the ORDER from above combineLatest
     */

    permissionObs$.subscribe(
      ([
        hasAiAccountsSummaryAccess,
        hasAiPitchBuilderAccess,
        hasCrossSellAccess,
        hasSupplierDataAccess,
        hasPatentsAccess,
        hasEnergyAndEmissionsAccess,
        hasZintGrowAccess,
        hasHubspotAccess,
        hasShareFilingsAccess,
        hasAIRAAccess,
        hasPAFAccess,
      ]) => {
        const gainedAIRAAccess = hasAIRAAccess && !this.featurePermissions.hasAIRAAccess;
        this.featurePermissions = {
          ...this.featurePermissions,
          hasAiAccountsSummaryAccess,
          hasAiPitchBuilderAccess,
          hasCrossSellAccess,
          hasSupplierDataAccess,
          hasPatentsAccess,
          hasEnergyAndEmissionsAccess,
          hasZintGrowAccess,
          hasHubspotAccess,
          hasShareFilingsAccess,
          hasAIRAAccess,
          hasPAFAccess,
        };

        // The profile request can finish before plan permissions arrive.
        // Load permitted modules when access becomes known in that order too.
        if (gainedAIRAAccess && this.company?.company_id) {
          this.getAIRAModuleData();
        }

        /**
         * the map below maps the nav and sections names to the features
         */
        this.permissionsCheckMap = {
          strategic_insights:
            this.featurePermissions.hasAiAccountsSummaryAccess,
          pitch_builder: this.featurePermissions.hasAiPitchBuilderAccess,
          cross_sell_opportunities: this.featurePermissions.hasCrossSellAccess,
          probable_suppliers: this.featurePermissions.hasSupplierDataAccess,
          patents_filed: this.featurePermissions.hasPatentsAccess,
          energy_and_emissions:
            this.featurePermissions.hasEnergyAndEmissionsAccess,
          magic_insights: this.featurePermissions.hasAIRAAccess,
          land_ownership: this.featurePermissions.hasPAFAccess,
        };
      }
    );
  }

  callPermissionOnlyDataEndpoints(): void {
    this.getSupplierData();
    this.getCompaniesShareholderIn();
    this.getCompanyShareholdersOf();
    this.getAIRAModuleData();
    this.getLandOwnershipData();
  }

  updateEndpointsStateSignals(signalKey: EndpointNames, dataLoaded = false) {
    this.endpointsDataFinishedLoading.update(state => ({
      ...state,
      [signalKey]: dataLoaded,
    }));
  }

  getCompanyProfilePageData(): void {
    if (this.companyNumber) {
      this.companyService.getCompanyProfileData(this.companyNumber).subscribe(
        data => {
          if (data) {
            this.company = data.company;
            this.getAvailableTableRowHeaders();
            this.processAssetsData();
            this.jobsListToRender = this.company.hiring_for;
            this.governmentContractsToRender =
              this.company.government_contracts_awarded;
            this.handleOfficersPSCsData();
            this.openSearchFromQueryParams();
            this.getConfirmationStatementsForChart();
            this.setDataSourceCurrentCompanyIds();
            if (this.company.latest_news.twitter_handle) {
              this.loadTwitterScript();
            }
            this.callPermissionOnlyDataEndpoints();
            this.setProfilePageTitle();
            // this.setupUserPreferences();
            this.getAvailableCRMs();
            this.updateEndpointsStateSignals(EndpointNames.ProfileData, true);
          }
        },
        error => {
          this.loadingError = error?.error?.message || 'An error has occurred';
          setTimeout(() => {
            window.location.replace('/dashboard');
          }, 2200);
        }
      );
      this.getCompanyUserDetails();
    }
  }

  setDataSourceCurrentCompanyIds(): void {
    if (this.companyNumberFromParent) return;
    this.dataStore.changeDataSourceCurrentCompanyIds([
      this.company?.company_id,
    ]);
  }

  setProfilePageTitle(): void {
    if (this.companyNumberFromParent) return;
    this.titleService.setTitle('ZINT - ' + this.company.company_overview?.name);
  }

  getAvailableCRMs(): void {
    this.crmService
      .getAvailableCRMs()
      .pipe(
        tap(data => {
          if (this.pubMethods.hasData(data)) {
            this.availableCRMList = data;
          }
        })
      )
      .subscribe();
  }

  getCompanyUserDetails(): void {
    /**
     * This method populates custom properties in overview
     * */
    this.companyService
      .getCompanyUserDetails(this.companyNumber)
      .subscribe(data => {
        this.userCustomProperties = data;
        this.userCustomProperties['hasSomeData'] =
          this.pubMethods.hasData(data);
        this.updateCustomColumnsDetails();
      });
  }

  goToMagicInsightsSection(): void {
    if (!this.permissionsCheckMap.magic_insights) {
      this.visitZintPage('magic_insights');
    }
    const firstMISection = this.allNavSections.find(nav =>
      nav.toLowerCase().includes('magic')
    );
    this.scrollToSectionAndExpand(firstMISection + '_section');
  }

  getConfirmationStatementsForChart(): void {
    if (!this.pubMethods.hasData(this.company.confirmation_statements)) return;

    this.company.confirmation_statements.forEach(confirmationYearObj => {
      const reduced: Record<string, any> =
        confirmationYearObj.shareholders_chart_data.reduce(
          (obj, curr) => {
            obj.legendLabels.push(curr.name);
            obj.series.push(curr.shares_held);
            obj.seriesTotal += curr.shares_held;
            return obj;
          },
          {
            legendLabels: [],
            series: [],
            lastUpdated: '',
            seriesTotal: 0,
          }
        );
      reduced.lastUpdated = confirmationYearObj.made_up_to;
      reduced.totalLabel = 'Shareholders';
      this.confirmedShareholdersData.push(reduced);
    });
  }

  handleConfirmationStatementsTabChange(changeEventData: any) {
    this.confirmationStatementsTabIndex = changeEventData.index;
  }

  getCompanyShareholdersOf(): void {
    if (!this.featurePermissions.hasShareFilingsAccess) return;
    this.updateEndpointsStateSignals(EndpointNames.ShareholdersOfData, false);

    this.companyService
      .getCompanyShareholdersOf(this.companyNumber)
      .subscribe(data => {
        this.companyShareholdersOf = data;
        this.updateEndpointsStateSignals(
          EndpointNames.ShareholdersOfData,
          true
        );
      });
  }

  getCompaniesShareholderIn(): void {
    if (!this.featurePermissions.hasShareFilingsAccess) return;
    this.updateEndpointsStateSignals(EndpointNames.ShareholdersInData, false);
    this.companyService
      .getCompaniesShareholderIn(this.companyNumber)
      .subscribe(data => {
        this.companiesShareholderIn = data;
        this.updateEndpointsStateSignals(
          EndpointNames.ShareholdersInData,
          true
        );
      });
  }

  getAIRAModuleData(isBackgroundRefresh = false): void {
    if (!this.featurePermissions.hasAIRAAccess) return;

    // A refresh after answers arrive must not re-run the first-load side effects.
    // Flipping the endpoint signal re-fires the constructor effect, which restores
    // preferences and scrolls/expands whatever the URL fragment points at, pulling
    // the reader away from the module they were waiting on.
    const isFirstLoad = !isBackgroundRefresh && this.requestUnlockMagic === false;

    if (isFirstLoad) {
      this.updateEndpointsStateSignals(EndpointNames.AIRAData, false);
    }
    this.isRefreshingAiraInBackground = !isFirstLoad;

    this.companyService
      .getAIRAData(this.companyNumber, true)
      .subscribe(data => {
        this.isRefreshingAiraInBackground = false;

        if (!data) return;

        // A failed request is caught and replayed as an empty array, so on a refresh
        // an empty payload would wipe the answers and the module list the reader
        // already has on screen.
        if (!isFirstLoad && !data.length) return;

        this.airaData = data;

        // These modules can be revealed or researched through the unified flow.
        this.magicInsightsModuleNamesIds = [];
        data.forEach((module: any) => {
          this.magicInsightsModuleNamesIds.push({
            id: module.id,
            name: module.module,
            isOrganisationModule: module.custom_module,
            questionCount: module.question_count || 0,
          });
        })

        if (this.airaData.length) {
          // The substitution is idempotent, so a refresh needs it too: a module
          // missing from allNavSections has no nav entry and a flex order of -1,
          // which floats its panel above the rest of the profile.
          this.processNavItemsWithAIRAModules();
        }
        if (isFirstLoad) {
          //we don't want to set the signal to true if this came from unlock request.
          this.updateEndpointsStateSignals(EndpointNames.AIRAData, true);
        }
      });
  }

  openGetInsights(moduleId: number): void {
    this.activeMagicInsightsModuleId = moduleId;
    this.getInsights?.open(moduleId);
  }

  handleGetInsightsCompleted(event: any): void {
    if (event?.isOrganisationModule) {
      this.handleMagicInsightsGenerated(event);
    } else {
      this.dataStore.unlockMagicModuleId.set(null);
      this.getAIRAModuleData(true);
      this.keepModulePanelOpen(event?.moduleId ?? this.activeMagicInsightsModuleId);
    }
  }

  /**
   * Unlocking and answering are both triggered from the panel header, which may be
   * collapsed. Leaving it shut reads as the request having been cancelled, so open it
   * and leave the scroll position alone.
   */
  private keepModulePanelOpen(moduleId: number): void {
    const sectionId = this.airaData?.find(module => module.id === moduleId)?.module;
    if (!sectionId) return;

    setTimeout(() => {
      const panel = this.findExpansionPanelById(`${sectionId}_section`);
      if (panel) panel.expanded = true;
    }, 100);
  }

  handleMagicInsightsGenerated(event: any): void {
    const response = event?.response || event;
    const airaRunId = response?.body?.aira_run_id;
    const moduleId = event?.moduleId || this.activeMagicInsightsModuleId;

    if (airaRunId && moduleId) {
      this.startPollingForModule(moduleId, airaRunId);
      this.keepModulePanelOpen(moduleId);
    }
  }

  private startPollingForModule(moduleId: number, airaRunId: string): void {
    this.stopPollingForModule(moduleId);

    let attemptCount = 0;

    // Check after five seconds for the normal fast path, then progressively
    // slow down while retaining the existing roughly ten-minute window.
    const pollSubscription = this.pageVisible$
      .pipe(
        switchMap(isVisible => {
          if (!isVisible) {
            return NEVER;
          }
          return concat(
            ...MAGIC_INSIGHTS_POLL_DELAYS_MS
              .slice(attemptCount)
              .map(delay => timer(delay))
          );
        }),
        takeUntil(this.destroy$),
        switchMap(() => {
          attemptCount++;
          this.updateAttemptCount(moduleId, attemptCount);

          return this.getPendingModuleStatus(moduleId, airaRunId);
        }),
        tap(status => this.handlePendingModuleStatus(moduleId, airaRunId, status))
      )
      .subscribe({
        complete: () => {
          if (this.pendingModuleGenerations.has(moduleId)) {
            this.stopPollingForModule(moduleId);
            this.snackbar.open(
              'Magic Insights generation is taking longer than expected. Please refresh the page later to check status.',
              'OK',
              { duration: 5000 }
            );
          }
        }
      });

    this.pendingModuleGenerations.set(moduleId, {
      airaRunId,
      pollSubscription,
      attemptCount: 0
    });
  }

  private updateAttemptCount(moduleId: number, count: number): void {
    const pending = this.pendingModuleGenerations.get(moduleId);
    if (pending) {
      pending.attemptCount = count;
    }
  }

  private stopPollingForModule(moduleId: number): void {
    const pending = this.pendingModuleGenerations.get(moduleId);
    if (pending) {
      this.pendingModuleGenerations.delete(moduleId);
      pending.pollSubscription.unsubscribe();
    }
  }

  private stopAllPolling(): void {
    this.pendingModuleGenerations.forEach((pending) => {
      pending.pollSubscription.unsubscribe();
    });
    this.pendingModuleGenerations.clear();
  }

  isModulePending(moduleId: number): boolean {
    return this.pendingModuleGenerations.has(moduleId);
  }

  /**
   * Only the first load may blank a module. A background refresh keeps the answers
   * already on screen until their replacements arrive.
   */
  isModuleLoading(moduleId: number): boolean {
    if (this.isModulePending(moduleId)) return true;
    return (
      this.loadingStatus.loadingAIRABetaResponse &&
      !this.isRefreshingAiraInBackground
    );
  }

  getPollingAttemptCount(moduleId: number): number {
    return this.pendingModuleGenerations.get(moduleId)?.attemptCount || 0;
  }

  private pollAllPendingModules(): void {
    this.pendingModuleGenerations.forEach((pending, moduleId) => {
      this.getPendingModuleStatus(moduleId, pending.airaRunId).subscribe(status => {
        this.handlePendingModuleStatus(moduleId, pending.airaRunId, status);
      });
    });
  }

  private getPendingModuleStatus(moduleId: number, airaRunId: string): Observable<any> {
    if (this.pendingStatusChecks.has(moduleId)) {
      return EMPTY;
    }
    this.pendingStatusChecks.add(moduleId);
    return this.companyService
      .getMagicInsightsRunStatus(airaRunId, this.companyNumber)
      .pipe(finalize(() => this.pendingStatusChecks.delete(moduleId)));
  }

  private handlePendingModuleStatus(moduleId: number, airaRunId: string, status: any): void {
    const pending = this.pendingModuleGenerations.get(moduleId);
    if (!pending || pending.airaRunId !== airaRunId || !status?.is_terminal) {
      return;
    }

    if (status.status === 'failed') {
      this.stopPollingForModule(moduleId);
      this.snackbar.open(
        'Magic Insights generation could not be completed.',
        'OK',
        { duration: 5000 }
      );
      return;
    }

    // getAIRAData raises the shared loading flag, which would otherwise swap every
    // module on the page over to a spinner while this poll resolves.
    this.isRefreshingAiraInBackground = true;

    this.companyService.getAIRAData(this.companyNumber, true).subscribe(data => {
      this.isRefreshingAiraInBackground = false;

      // A failed request is caught and replayed as an empty array, which carries no
      // answers to apply and must not replace what is already on screen.
      if (!Array.isArray(data) || !data.length) {
        return;
      }

      const moduleData = data.find(module => module.id === moduleId);
      const answersAreAvailable = this.pubMethods.hasData(moduleData?.responses);
      if (!answersAreAvailable && status.status !== 'completed_with_issues') {
        return;
      }

      this.airaData = data;
      this.stopPollingForModule(moduleId);

      const message = status.status === 'completed_with_issues'
        ? 'Magic Insights generation completed with some answers unavailable.'
        : 'Magic Insights generation complete!';
      this.snackbar.open(message, 'OK', { duration: 5000 });
    });
  }

  unlockMagicModule(moduleId: number): void {
    this.requestUnlockMagic = true;

    this.companyService
      .unlockMagicInsightsModules(moduleId, [this.company.company_id], null, null)
      .subscribe(response => {
        this.dataStore.unlockMagicModuleId.set(null);
        this.getAIRAModuleData();
      });
  }

  toggleAllInsights(): void {
    this.dataStore.updateHideEmptyMIInsights();
    const hiddenState = this.dataStore.hideEmptyInsights();
    this.snackbar.open(
      `Empty Magic Insights will now be ${hiddenState ? 'hidden' : 'shown'} on all profiles`,
      'OK',
      {
        duration: 2200,
      }
    );
  }

  setMISortBy(sortString: MISortString): void {
    this.magicInsightsSortString = sortString;
    if (sortString === null) {
      localStorage.removeItem('magicInsightsSortBy');
      return;
    }
    localStorage.setItem('magicInsightsSortBy', this.magicInsightsSortString);
  }

  processNavItemsWithAIRAModules(): void {
    if (!this.airaData.length) return;

    const moduleNames = this.airaData.map(obj => obj.module);
    const previousModuleNames = new Set(this.airaSpecificNavItems);
    const sections = [...this.allNavSections];

    // 'magic_insights' is only present until the first substitution; afterwards the
    // module names occupy its slot. Splicing on indexOf('magic_insights') a second
    // time ran at -1, which dropped the final section (sending its flex order to -1
    // and reshuffling the page) and duplicated the module names in the nav.
    const insertAt = sections.findIndex(
      section => section === 'magic_insights' || previousModuleNames.has(section)
    );

    if (insertAt === -1) {
      // The nav sections request may not have resolved yet. Appending still puts the
      // module names in allNavSections, so their panels take a real flex order rather
      // than the -1 that would float them above the whole profile.
      this.airaSpecificNavItems = moduleNames;
      this.allNavSections = [...sections, ...moduleNames];
      return;
    }

    // Only the contiguous run at insertAt belongs to Magic Insights. Counting every
    // match in the array would splice out unrelated sections whenever a module shares
    // its name with one of them.
    let replaceCount = 1;
    if (sections[insertAt] !== 'magic_insights') {
      replaceCount = 0;
      while (previousModuleNames.has(sections[insertAt + replaceCount])) {
        replaceCount++;
      }
    }

    sections.splice(insertAt, replaceCount, ...moduleNames);

    this.airaSpecificNavItems = moduleNames;
    this.allNavSections = sections;
  }

  refreshZintGrowColumns(): void {
    this.zintGrowColumns = this.userCustomProperties.custom_columns.filter(
      col => col.is_zint_grow_column === true
    );
    this.zintGrowColumnsFollowing =
      this.userCustomProperties.custom_columns.filter(
        col =>
          col.is_zint_grow_column === true &&
          col.value === this.accountDetails.email
      );
  }

  initFollowZintGrow(): void {
    if (this.companyNumberFromParent) {
      this.openFullProfilePage();
      return;
    }
    const editableZGCols = this.zintGrowColumns.filter(col => col.can_edit);

    if (!editableZGCols.length) {
      this.messageService.showAndHideAfterDelay(
        'You need edit access to a Zint Grow column.',
        3100
      );
    }

    if (editableZGCols?.length === 1) {
      this.followCompanyInZintGrow(editableZGCols[0]);
    } else if (editableZGCols?.length > 1) {
      $('#followZintGrowModal').modal('show');
    }
  }

  followCompanyInZintGrow(zintGrowColumnAdding: Record<string, any>): void {
    this.zintGrowSubscriptionService
      .updateUserColumnCompany(
        zintGrowColumnAdding.id,
        this.company['company_id'],
        this.accountDetails.userId
      )
      .subscribe(
        data => {
          if (data.value === this.accountDetails.userId) {
            this.snackbar.open('Company added to Zint Grow!', 'X', {
              duration: 2200,
            });
            this.zintGrowColumnsFollowing.push(zintGrowColumnAdding);
            $('#followZintGrowModal').modal('hide');
          }
        },
        error => {
          if (error?.error?.hasOwnProperty('nonFieldErrors')) {
            this.messageService.show(
              'This column may already be associated with a different user in your team, try a different column'
            );
          }
        }
      );
  }

  unfollowCompanyInZintGrow(): void {
    if (this.zintGrowColumnsFollowing) {
      const zintGrowColumnRemoving = this.zintGrowColumnsFollowing[0];
      this.hasColumnEditAccess = zintGrowColumnRemoving.can_edit;
      if (!this.hasColumnEditAccess) {
        this.requestColumnEditAccessForZintGrow();
        return;
      }
      this.companyService
        .updateCustomOptionColumn(
          zintGrowColumnRemoving.id,
          this.company.company_id,
          ''
        )
        .subscribe(data => {
          this.zintGrowColumnsFollowing = this.zintGrowColumnsFollowing.filter(
            col => col.id !== zintGrowColumnRemoving.id
          );
          this.snackbar.open('Company removed from Zint Grow', 'X', {
            duration: 2200,
          });
        });
    }
  }

  requestColumnEditAccessForZintGrow(): void {
    this.messageService.show(
      'You do not have permission to do this. Please ask your team superuser or contact customer success via chat.'
    );

    zE('messenger', 'open');
  }

  openTagOrCRMModal(modalToOpen: 'Tag' | 'CRM', crmName = ''): void {
    if (this.companyNumberFromParent) {
      this.openFullProfilePage();
      return;
    }

    if (modalToOpen === 'CRM') {
      this.crmExporterName = crmName;
      this.cdr.detectChanges();
    }

    const modalElement =
      modalToOpen === 'Tag' ? '#tagModal' : `#exportTo${modalToOpen}Modal`;

    $(modalElement).modal('show');
  }

  getCRMIcon(crmType: string): string {
    const fontAwesomeCRMs = new Set(['hubspot', 'salesforce']);

    if (fontAwesomeCRMs.has(crmType)) {
      return `<i class="fab fa-${crmType}"></i>`;
    }

    const svgPath = `/assets/tech-icons/${crmType}.svg`;
    return `<img src="${svgPath}" alt="${crmType.charAt(0).toUpperCase() + crmType.slice(1)}">`;
  }

  getAllNavSections(): void {
    this.companyService.getAllCompanySections().subscribe(data => {
      if (data) {
        this.navSectionsInUserOrder = data['sections_order'] || [];
        const allSections = data['all_sections'] || [];

        const uniqueSections = new Set<string>(this.navSectionsInUserOrder);

        allSections.forEach(section => uniqueSections.add(section));

        this.allNavSections = Array.from(uniqueSections);
      }
    });
  }

  getSectionIcon(section: string): string {
    return this.pubMethods.renderNavSectionIcon(section);
  }

  hasAnyStrategicInsights(insightObj: Record<string, any>): boolean {
    const meaningfulInsightsKeys = [
      'bullets',
      'business_model',
      'future_events',
      'pains_challenges',
      'strategic_goals',
    ];

    return meaningfulInsightsKeys.some(key =>
      this.pubMethods.hasData(insightObj[key])
    );
  }

  getSectionPolledCount(paramsObj: {
    countObj: Object | Array<any> | string;
    countNestedObj?: boolean;
    isAssetData?: boolean;
    isImpExpData?: boolean;
    isEnergyData?: boolean;
    isStrategicInsights?: boolean;
  }): number {
    const {
      countObj,
      countNestedObj = true,
      isStrategicInsights = false,
      isAssetData = false,
      isImpExpData = false,
      isEnergyData = false,
    } = paramsObj;
    let count = 0;
    if (typeof countObj === 'string') return (count += countObj.length ? 1 : 0);

    if (Array.isArray(countObj)) return (count += countObj.length);

    if (typeof countObj === 'object' && countObj !== null) {
      if (isStrategicInsights) {
        // count the insights objects with only meaningful data
        let insightsCount = 0;
        Object.values(countObj).forEach((arr: Array<any>) => {
          arr.forEach(obj => {
            insightsCount += this.hasAnyStrategicInsights(obj) ? 1 : 0;
          });
        });
        return insightsCount;
      }

      if (isImpExpData) {
        // tally up the values
        let impExpCount = 0;
        Object.values(countObj).forEach((arr: Array<any>) =>
          arr.forEach(obj => {
            impExpCount += Number(obj.value);
          })
        );
        return impExpCount;
      }

      if (isEnergyData) {
        // count the unique years
        if (!countObj['energy'] && !countObj['emissions']) return 0;

        const uniqueYearsOnly = new Set(
          countObj['energy']
            .concat(countObj['emissions'])
            .map(obj => obj.made_up_to)
        );
        return uniqueYearsOnly.size;
      }

      if (countNestedObj) {
        Object.values(countObj).forEach(val => {
          if (val) {
            count += this.getSectionPolledCount({
              countObj: val,
              countNestedObj: !isAssetData, // count the nestedObjs unless it is assetData
            });
          }
        });
      } else {
        count += this.pubMethods.hasData(countObj) ? 1 : 0;
      }
    }

    return count;
  }

  getSectionOrder(section: string): number {
    return this.allNavSections.indexOf(section);
  }

  showAllNavSections(): void {
    this.toggleAllNavSections = !this.toggleAllNavSections;
    this.isReordering = false;
  }

  indicateCurrentNavItemInView(nav: string): object {
    const indicatorStyle = {
      'padding-right': '0.5rem',
      'padding-left': '0.5rem',
      'padding-bottom': '0.2rem',
      'border-bottom': '3px solid var(--zint-vivid-blue)',
    };

    const currentNavStrFromSection = this.sectionInView?.slice(0, -8);

    return nav === currentNavStrFromSection ? indicatorStyle : {};
  }

  handleNavStyle(sectionName: string): string {
    const lowerCaseName = sectionName.toLowerCase();

    const anomaliesMap: Partial<Record<CompanyPageSectionEnums, string>> = {
      events_attending: 'events',
    };

    const defaultSections = ['company_overview', 'corporate_structure'];

    const simpleStringsForLengthCheck: string[] = Object.values(
      CompanyPageSectionEnums
    ).filter(
      key =>
        !Object.keys(this.permissionsCheckMap).includes(key) &&
        !Object.keys(anomaliesMap).includes(key)
    );

    let lengthCheckStr =
      simpleStringsForLengthCheck.find(str => str === lowerCaseName) ||
      anomaliesMap[lowerCaseName];

    if (defaultSections.includes(lowerCaseName)) return '';

    if (lengthCheckStr) {
      return this.handleLengthCheckStr(lengthCheckStr);
    }

    if (Object.keys(this.permissionsCheckMap).includes(lowerCaseName)) {
      return this.handlePermissionNavSection(lowerCaseName);
    }

    if (lowerCaseName.includes('magic')) {
      return this.handlePermissionNavSection(lowerCaseName);
    }

    return '';
  }

  handleLengthCheckStr(lengthCheckStr: string): string {
    const counterKey =
      lengthCheckStr === 'events' ? 'events_attending' : lengthCheckStr;

    if (lengthCheckStr === 'asset_breakdown') {
      this.navSectionPolledCount[counterKey] = this.getSectionPolledCount({
        countObj: this.company[lengthCheckStr],
        isAssetData: true,
      });
    } else if (lengthCheckStr === 'financials') {
      this.processFinancialsCount();
    } else if (
      lengthCheckStr === 'legal_entity_identifier' ||
      lengthCheckStr === 'website'
    ) {
      this.navSectionPolledCount[counterKey] = this.getSectionPolledCount({
        countObj: this.company[lengthCheckStr],
        countNestedObj: false,
      });
    } else if (lengthCheckStr === 'imports_&_exports') {
      this.navSectionPolledCount[counterKey] = this.getSectionPolledCount({
        countObj: this.company[lengthCheckStr],
        isImpExpData: true,
      });
    } else if (lengthCheckStr === 'government_contracts_awarded') {
      this.navSectionPolledCount[counterKey] =
        this.governmentContractsToRender.length >= 20
          ? `~${this.governmentContractsToRender.length}`
          : this.governmentContractsToRender.length;
    } else if (lengthCheckStr === 'share_information') {
      this.navSectionPolledCount['share_information'] =
        this.company?.share_allotments?.length +
        this.companiesShareholderIn?.length +
        this.companyShareholdersOf?.length;

      const noNavForShareInfo =
        this.company?.share_allotments?.length < 1 &&
        this.companiesShareholderIn?.length < 1 &&
        this.companyShareholdersOf?.length < 1;
      return noNavForShareInfo ? 'nav-no-data' : '';
    } else if (
      lengthCheckStr === 'shareholders' &&
      this.pubMethods.hasData(this.company?.confirmation_statements)
    ) {
      const latestShareholdersTotal =
        this.company?.confirmation_statements[0]?.shareholders_chart_data
          ?.length;
      this.navSectionPolledCount[counterKey] = latestShareholdersTotal;
      return latestShareholdersTotal ? '' : 'nav-no-data';
    } else {
      this.navSectionPolledCount[counterKey] = this.getSectionPolledCount({
        countObj: this.company[lengthCheckStr],
      });
    }

    return this.pubMethods.hasData(this.company[lengthCheckStr])
      ? ''
      : 'nav-no-data';
  }

  handlePermissionNavSection(sectionName: string): string {
    const permissionCheckOnly = [
      'pitch_builder',
      'cross_sell_opportunities',
      'magic_insights',
    ];

    if (permissionCheckOnly.includes(sectionName)) {
      return this.permissionsCheckMap[sectionName] ? '' : 'nav-no-permission';
    }

    if (sectionName.toLowerCase().includes('magic')) {
      //TODO: remove nav-cta on 15/05/2025
      if (this.permissionsCheckMap.magic_insights) {
        return this.pubMethods.hasData(this.airaData)
          ? 'nav-cta heartbeat'
          : 'nav-no-data';
      } else return 'nav-no-permissions';
    }

    if (sectionName === 'land_ownership') {
      if (this.permissionsCheckMap.land_ownership) {
        this.navSectionPolledCount['land_ownership'] =
          this.getSectionPolledCount({ countObj: this.landOwnershipData });

        return this.pubMethods.hasData(this.airaData) ? '' : 'nav-no-data';
      } else return 'nav-no-permissions';
    }

    if (sectionName === 'strategic_insights') {
      if (this.permissionsCheckMap.strategic_insights) {
        this.navSectionPolledCount['strategic_insights'] =
          this.getSectionPolledCount({
            countObj: this.company['strategic_insights'],
            isStrategicInsights: true,
          });

        return this.pubMethods.hasData(this.company['strategic_insights'])
          ? ''
          : 'nav-no-data';
      } else return 'nav-no-permissions';
    }

    if (sectionName === 'patents_filed') {
      if (this.permissionsCheckMap.patents_filed) {
        this.navSectionPolledCount['patents_filed'] =
          this.getSectionPolledCount({
            countObj: this.company['patents_filed'],
          });

        return this.pubMethods.hasData(this.company['patents_filed'])
          ? ''
          : 'nav-no-data';
      } else return 'nav-no-permissions';
    }

    if (sectionName === 'probable_suppliers') {
      if (this.permissionsCheckMap.probable_suppliers) {
        this.navSectionPolledCount['probable_suppliers'] =
          this.supplierData?.length - 1;

        return this.supplierData?.length > 1 ? '' : 'nav-no-data';
      } else {
        return 'nav-no-permissions';
      }
    }

    if (sectionName === 'energy_and_emissions') {
      if (this.permissionsCheckMap.energy_and_emissions) {
        this.navSectionPolledCount['energy_and_emissions'] =
          this.getSectionPolledCount({
            countObj: this.company['energy_and_emissions'],
            isEnergyData: true,
            countNestedObj: false,
          });

        return this.pubMethods.hasData(this.company['energy_and_emissions'])
          ? ''
          : 'nav-no-data';
      } else {
        return 'nav-no-permissions';
      }
    }

  }

  showPremiumIcon(navName: string): boolean {
    /* returns true only if NEEDS permission but LACKS it */

    const lowerCaseNav = navName.toLowerCase();

    if (!this.permissionsCheckMap.hasOwnProperty(lowerCaseNav)) return false;

    if (this.permissionsCheckMap.hasOwnProperty(lowerCaseNav)) {
      // return the flipped boolean
      return !this.permissionsCheckMap[lowerCaseNav];
    }
  }

  visitZintPage(nav: string): void {
    const zintPageMap: Partial<Record<CompanyPageSectionEnums, string>> = {
      pitch_builder: 'https://www.zint.io/platform-features/pitch-builder',
      strategic_insights:
        'https://www.zint.io/platform-features/zint-strategic-summary',
      energy_and_emissions: 'https://www.zint.io/use-cases/energy',
      cross_sell_opportunities:
        'https://www.zint.io/platform-features/cross-sell-tools',
      patents_filed: 'https://www.zint.io/platform-features/pipeline-builder',
      probable_suppliers: 'https://www.zint.io',
      magic_insights: 'https://www.zint.io/platform-features/magicinsights',
      land_ownership: 'https://www.zint.io/use-cases/tax-r-and-d',
    };

    window.open(zintPageMap[nav], '_blank');
  }

  toggleFocusView(): void {
    this.isFocusView = !this.isFocusView;
    this.toggleAllNavSections = false;

    if (this.isFocusView === true) {
      localStorage.setItem(
        'profileFocusView',
        JSON.stringify(this.isFocusView)
      );

      this.snackbar.open(
        'Preference setting saved. Toggling this off will remove the setting.',
        'Okay',
        {
          duration: 6000,
        }
      );
    }

    if (this.isFocusView === false) {
      localStorage.removeItem('profileFocusView');
      this.scrollToSectionAndExpand(this.sectionInView);
    }
  }

  getFocusedSection(sectionId: string): string {
    if (!this.isFocusView) return 'block';
    return this.sectionInView === sectionId ? 'block' : 'none';
  }

  reOrderNavSections(): void {
    if (this.companyNumberFromParent) {
      this.openFullProfilePage('#see-all-navs');
      return;
    }

    let profilePageSelf = this;
    this.isReordering = true;
    this.toggleAllNavSections = true;

    let excludedNavs = ['company_overview', ...this.airaSpecificNavItems];
    let navSectionsToReorder = this.allNavSections.filter(
      nav => !excludedNavs.includes(nav)
    );

    if (
      this.airaSpecificNavItems.length &&
      !this.allNavSections.includes('magic_insights')
    ) {
      // minus company_overview
      const magicInsightsOrderIndex =
        this.navSectionsInUserOrder.indexOf('magic_insights') - 1;

      navSectionsToReorder.splice(magicInsightsOrderIndex, 0, 'magic_insights');
    }

    this.tempReOrderingList = navSectionsToReorder;

    $('#reOrderSectionsModal').modal('show');

    $('#reOrderSectionsModal').on('hidden.bs.modal', function () {
      profilePageSelf.isReordering = false;
    });
  }

  saveReOrderedSections(): void {
    if (this.isReordering) {
      this.isReordering = false;
      this.allNavSections = ['company_overview', ...this.tempReOrderingList];
      $('#reOrderSectionsModal').modal('hide');

      this.companyService
        .saveUserPreferredNavOrder(this.allNavSections)
        .subscribe(
          success => {
            if (success) {
              this.processNavItemsWithAIRAModules();
            }
          },
          error => {
            this.messageService.show(
              'Sorry there was an error saving your reorder request'
            );
          }
        );
    }
  }

  dragSort(event: CdkDragDrop<any>): void {
    moveItemInArray(
      this.tempReOrderingList,
      event.previousIndex,
      event.currentIndex
    );
  }

  scrollAndCollapseAllNavs(sectionId: string): void {
    this.toggleAllNavSections = false;

    setTimeout(() => {
      this.scrollToSectionAndExpand(sectionId);
    }, 200);
  }

  getTagsTotal(): number {
    const {
      tags = [],
      team_member_tags = [],
      organisation_tags = [],
    } = this.userCustomProperties;

    const filtered = [
      ...tags,
      ...team_member_tags,
      ...organisation_tags,
    ].filter(tag => !tag.is_propensity);

    return filtered.length;
  }

  removeTag(tagId): void {
    this.taggingService
      .tagOrUntagCompanies(tagId, [this.company['company_id']], null, false)
      .subscribe(
        data => {
          if (data && data.total === 1) {
            this.getCompanyUserDetails();
          } else {
            this.messageService.show('Could not remove tag.');
          }
        },
        error => {
          this.messageService.show('Could not remove tag.');
        }
      );
  }

  updateCustomColumnsDetails(): void {
    this.teamMembers = this.userCustomProperties?.team_members;
    if (this.userCustomProperties.custom_columns) {
      this.sortCustomProperties(this.userCustomProperties.custom_columns);

      this.userCustomProperties.custom_columns.forEach(col => {
        if (col.column_type === 'User') {
          col['options'] = this.teamMembers;
        }
      });
      this.refreshZintGrowColumns();
    }

    //reset the signal back to default state
    this.dataStore.companyTagsUpdated.update(flag => (flag = false));
  }

  sortCustomProperties(customColumns: Record<string, any>[]): void {
    // Sort propensity columns to appear first
    customColumns.sort((a, b) => {
      if (a.is_propensity && !b.is_propensity) {
        return -1; // a first
      } else if (!a.is_propensity && b.is_propensity) {
        return 1; // a not first
      } else {
        return 0; // original order
      }
    });
  }

  getPropensityComponentStyle(
    scoreTag: Record<string, any>
  ): Record<string, any> {
    // prettier-ignore
    const styleObj = {
      'background-color':
        scoreTag?.propensity_component_weighting >= 0
          ? '#' + scoreTag?.colour
          : 'var(--zint-red-600)',
      opacity: this.propensityOpacityPipe.transform(
        scoreTag?.propensity_component_weighting
      ),
      cursor: this.showPropensitySearchLink(scoreTag) ? 'pointer' : 'auto',
    };

    return styleObj;
  }

  showPropensitySearchLink(propensityTag): boolean {
    const params = propensityTag.propensity_component_params;
    if (
      params.homepageSearchTerm ||
      params.fullWebsiteSearchTerm ||
      params.accountsSearchTerm ||
      params.newsContaining
    ) {
      return true;
    } else {
      return false;
    }
  }

  getProductsServicesSearchHref(term: string): string {
    return `/dashboard?summarySemanticSearchTerm=${encodeURIComponent(term)}`;
  }

  openSearchFromQueryParams() {
    const params = this.route.snapshot.queryParams;

    if (params.accountsSearch) {
      if (params.accountsDate) {
        this.accountsDateSelected = params.accountsDate;
      }
      this.accountsSearchText = params.accountsSearch;

      this.scrollAndModalSearch('financials_section', 'accounts-search');
    } else if (params.fullWebsiteSearch) {
      this.fullWebsiteSearchTerm = params.fullWebsiteSearch;

      this.scrollAndModalSearch('website_section', 'website-search');
    } else if (params.companyNewsSearch) {
      if (this.pubMethods.hasData(this.company?.latest_news?.news)) {
        this.scrollToSectionAndExpand('latest_news_section');
        this.thirdPartyNewsSearchText = params.companyNewsSearch;
      }
    } else if (params.selfPublishedCompanyNewsSearch) {
      if (
        this.pubMethods.hasData(this.company?.latest_news?.self_published_news)
      ) {
        this.scrollToSectionAndExpand('latest_news_section');
        this.selfPublishedCompanyNewsSearchText =
          params.selfPublishedCompanyNewsSearch;
      }
    }
  }

  openSearchPreview(propensityTag): void {
    const params = propensityTag.propensity_component_params;

    if (params.homepageSearchTerm || params.fullWebsiteSearchTerm) {
      if (params.homepageSearchTerm) {
        this.fullWebsiteSearchTerm = params.homepageSearchTerm;
      }
      if (params.fullWebsiteSearchTerm) {
        this.fullWebsiteSearchTerm = params.fullWebsiteSearchTerm;
      }

      this.scrollAndModalSearch('website_section', 'website-search');
    }

    if (params.accountsSearchTerm) {
      this.accountsSearchText = params.accountsSearchTerm;

      this.scrollAndModalSearch('financials_section', 'accounts-search');
    }

    if (params.newsContaining) {
      this.scrollToSectionAndExpand('latest_news_section');
      if (params.newsSource === 'selfPublished') {
        this.selfPublishedCompanyNewsSearchText = params.newsContaining;
      } else {
        this.thirdPartyNewsSearchText = params.newsContaining;
      }
    }
  }

  private scrollAndModalSearch(
    sectionId: string,
    searchType: ProfileOpenSearchType
  ) {
    this.scrollToSectionAndExpand(sectionId);

    if (searchType === 'accounts-search') {
      this.accountsSearch();
    } else if (searchType === 'website-search') {
      this.fullWebsiteSearch();
    }

    setTimeout(() => {
      this.openSearchModal(searchType);
    }, 500);
  }

  fullWebsiteSearch(searchTerm?: string): void {
    if (searchTerm) {
      this.fullWebsiteSearchTerm = searchTerm;
    }

    if (!this.fullWebsiteSearchTerm.trim().length) return;

    this.companyService
      .runFullWebsiteSearch(this.companyNumber, this.fullWebsiteSearchTerm)
      .subscribe(data => (this.fullWebsiteResults = data.matching_webpages));
  }

  accountsSearch(searchObj?: Record<string, any>): void {
    if (searchObj) {
      this.accountsSearchText = searchObj.searchTerm;
      this.accountsDateSelected = searchObj.selectedDate;
    }

    if (!this.accountsSearchText.trim().length) return;

    this.companyService
      .searchCompanyAccounts(
        this.companyNumber,
        this.accountsSearchText,
        this.accountsDateSelected
      )
      .subscribe(data => {
        this.accountsResults = data;

        if (this.accountsResults['accounts_results']?.length) {
          this.accountsResults['accounts_results'].forEach(
            (account: Record<string, any>) => {
              account.processedPageNumber =
                this.processAccountsResultPageNumber(account);
            }
          );
        }
      });
  }

  processAccountsResultPageNumber(
    accountObj: Record<string, any>
  ): string | number {
    if (!accountObj) return '';
    const { accounts_type, page_number } = accountObj;

    if (!accounts_type) return page_number;

    if (accounts_type === 'ocr') {
      const pageNumberPlusOne = Number(page_number) + 1;
      return pageNumberPlusOne;
    } else {
      return page_number;
    }
  }

  showUpdatedCustomAttrsMessage(): void {
    this.customAttrsUpdated = true;
    setTimeout(() => {
      this.customAttrsUpdated = false;
    }, 1500);
  }

  openInsightsModal(): void {
    if (this.companyNumberFromParent) {
      this.openFullProfilePage('#strategic_insights_section');
      return;
    }
    $('#AiInsightsModal').modal('show');
  }

  generateAiPitch(): void {
    this.companyService.validateAIPitchBuilderSettings().subscribe(data => {
      if (data.settings_configured === true) {
        if (data.counter === 0) {
          this.pubMethods.showWarningMessageWithoutAutoDisappear(
            'You do not have any AI credits on your account. Please contact customer success to add more.'
          );
          zE('messenger', 'open');
        } else {
          this.getAiPitch();
        }
      } else {
        if (this.accountDetails.isSuperUser) {
          this.pubMethods.showWarningMessageWithoutAutoDisappear({
            text: 'We were not able to auto-generate your AI configuration. To configure it, navigate',
            action: { label: 'here', href: '/pitch-builder' },
          });
        } else {
          this.pubMethods.showInfoMessage(
            'Please ask your superuser to configure your AI settings or contact customer success through chat for assistance.'
          );
        }
      }
    });
  }

  getAiPitch() {
    this.loadingStatus['loadingAIPitch'] = true;
    const source = new EventSource(
      baseUrl + '/get_ai_pitch/' + this.companyNumber,
      { withCredentials: true }
    );
    source.addEventListener('message', message => {
      if (message.data) {
        const data = JSON.parse(message.data);
        if (data.content) {
          this.companyAIPitch += data.content;
        }
      } else {
        this.loadingStatus['loadingAIPitch'] = false;
        source.close();
      }
    });

    source.addEventListener('error', e => {
      this.loadingStatus['loadingAIPitch'] = false;

      source.close();
    });
  }


  openAllKeyPeopleModal(): void {
    if (this.companyNumberFromParent) {
      this.openFullProfilePage('#key_people_section');
      return;
    }
    $('#allKeyPeopleModal').modal('show');
  }

  openSearchModal(openSearchType: ProfileOpenSearchType): void {
    if (this.companyNumberFromParent) {
      const sectionId =
        openSearchType === 'accounts-search'
          ? '#financials_section'
          : '#website_section';

      this.openFullProfilePage(sectionId);
      return;
    }

    if (openSearchType === 'website-search') {
      this.openSearchModalTitle = 'Search Website';
      this.currentOpenSearchType = 'website-search';
    } else if (openSearchType === 'accounts-search') {
      this.yearAccountsMadeUpToDates = this.company.financials.made_up_to_dates;
      this.openSearchModalTitle = 'Search Accounts';
      this.currentOpenSearchType = 'accounts-search';
    }

    // Open the modal
    const modalElement = document.getElementById('openSearchModal');
    if (modalElement) {
      $(modalElement).modal('show');
    }
  }

  getAvailableTableRowHeaders(): void {
    /**
      Take all non-null keys of every energy or emissions objects and remove dupes.
      Then remove non row headers e.g submission_details for accounts and low_energy for energy etc.
     */

    const nonEnergyItems = ['low_energy', 'made_up_to'];

    if (this.company.energy_and_emissions?.energy?.length) {
      this.availableEnergyTerms = Array.from(
        new Set(
          this.company.energy_and_emissions.energy.flatMap(energyObj =>
            Object.keys(energyObj).filter(
              key => !nonEnergyItems.includes(key) && energyObj[key] !== null
            )
          )
        )
      );
    }

    if (this.company.energy_and_emissions?.emissions?.length) {
      this.availableEmissionsTerms = Array.from(
        new Set(
          this.company.energy_and_emissions.emissions.flatMap(emissionsObj =>
            Object.keys(emissionsObj).filter(
              key => !nonEnergyItems.includes(key) && emissionsObj[key] !== null
            )
          )
        )
      );
    }
  }

  processFinancialsCount(): void {
    if (this.pubMethods.hasData(this.company.financials)) {
      //just count the length of first columns array as they will be the same for others
      this.navSectionPolledCount['financials'] = Object.values(
        this.company.financials.accounts
      )[0]?.['columns']?.length;

      this.processFinancialsProviders();
    }
  }

  processFinancialsProviders(): void {
    if (this.pubMethods.hasData(this.company.financials?.financial_providers)) {
      const { banker, accountant, auditor, lawyer } =
        this.company.financials.financial_providers;

      this.companyBanker = this.splitByCommaOrLineBreak(banker);
      this.companyAccountant = this.splitByCommaOrLineBreak(accountant);
      this.companyAuditor = this.splitByCommaOrLineBreak(auditor);
      this.companyLawyer = this.splitByCommaOrLineBreak(lawyer);
    }
  }

  splitByCommaOrLineBreak(str: string): string {
    if (!str) return '';

    if (str.includes('\n')) {
      const split = str.split(/\n/);

      const [first, second, ...leftover] = split;
      return `${first} ${second}\n${leftover.join(' ')}`;
    } else {
      const split = str.split(',');
      const [first, ...leftover] = split;
      return `${first}\n${leftover.join(' ')}`;
    }
  }

  processAssetsData(): void {
    /**
     * Loop through the asset_breakdown obj to collect keys for both tangible and intangible assets.
     * Based on the length of these keys arrays, return whether "both" or only "tangible", or only "intangible" assets are available.
     * The processed data is saved to availableAssetsData which is then used to dynamically populate the assets table header columns.
     */

    if (!this.pubMethods.hasData(this.company.asset_breakdown)) return;

    let tangibleKeys = [];
    let intangibleKeys = [];

    Object.values(this.company.asset_breakdown).forEach(assetYearObj => {
      if (assetYearObj['tangible']) {
        Object.keys(assetYearObj['tangible']).forEach(key =>
          tangibleKeys.push(key)
        );
      }
      if (assetYearObj['intangible']) {
        Object.keys(assetYearObj['intangible']).forEach(key =>
          intangibleKeys.push(key)
        );
      }
    });

    let availableAssetType = '';

    if (!!tangibleKeys?.length && !!intangibleKeys?.length) {
      availableAssetType = 'both';
    } else {
      availableAssetType = tangibleKeys.length ? 'tangible' : 'intangible';
    }
    this.availableAssetsData = {
      availableAssetType,
      tangible: Array.from(new Set(tangibleKeys)),
      intangible: Array.from(new Set(intangibleKeys)),
    };
  }

  performJobSearch(searchTerm?: string): void {
    if (!searchTerm.trim().length) {
      this.resetSearch('jobSearch');
      return;
    }

    this.jobSearchTerm = searchTerm;

    this.companyService
      .searchCompanyJobs(this.companyNumber, this.jobSearchTerm)
      .subscribe(data => {
        if (data) {
          this.jobsListToRender = data.matching_jobs?.length
            ? data.matching_jobs
            : [{ summary: 'No jobs found' }];
        }
      });
  }

  resetSearch(option?: string): void {
    if (option === 'jobSearch') {
      this.jobSearchTerm = '';
      this.jobsListToRender = this.company.hiring_for;
    }
  }

  confirmDownloadAllGovernmentContracts(): void {
    if (this.companyNumberFromParent) {
      this.openFullProfilePage('#government_contracts_awarded_section');
      return;
    }

    if (!this.allGovernmentContracts.length) {
      this.getAllGovernmentContracts();
    }

    $('#confirmDownloadModal').modal('show');
  }

  getAllGovernmentContracts(showAll?: boolean): void {
    this.companyService
      .getAllGovernmentContracts(this.companyNumber)
      .subscribe(data => {
        if (data) {
          this.allGovernmentContracts = data;

          if (showAll) {
            this.governmentContractsToRender = this.allGovernmentContracts;
          }
        }
      });
  }

  downloadAllGovtContracts(): void {
    window.open(
      baseUrl +
        '/download_government_contracts?companyNumber=' +
        this.companyNumber
    );
  }

  hasLowEnergyUse(energyData: Record<string, any>[]): boolean {
    return energyData.some(obj => obj.low_energy === true);
  }

  getSupplierData() {
    if (!this.featurePermissions.hasSupplierDataAccess) return;
    this.updateEndpointsStateSignals(EndpointNames.SupplierData, false);
    const supplierDataPageNumber = 1;
    this.companyService
      .getSupplierGraph(this.companyNumber, supplierDataPageNumber)
      .subscribe(data => {
        if (data) {
          this.supplierData = data.company_mentions_graph?.nodes;
          this.updateEndpointsStateSignals(EndpointNames.SupplierData, true);
        }
      });
  }

  handleOfficersPSCsData(): void {
    /**merge two arrays together */
    this.officersPSCsData = [
      ...this.company.corporate_officers?.officers,
      ...this.company.corporate_officers?.pscs,
    ];
  }

  openImportsExportsModal(modalType: 'imports' | 'exports'): void {
    if (this.companyNumberFromParent) {
      this.openFullProfilePage('#imports_&_exports_section');
      return;
    }
    this.impExpModalType = modalType;
    $('#impExpModal').modal('show');
  }

  getCompanyDetailedImportsExportsData(): void {
    this.companyService
      .getCompanyDetailedImportsExports(this.companyNumber)
      .subscribe(data => {
        this.detailedExportsData = data['detailed_exports'];
        this.detailedImportsData = data['detailed_imports'];
      });
  }

  getVehicleLicenceStatus(rawStatus: string): string {
    return rawStatus.split('_')[1];
  }

  formatVehicleLicenceAddresses(rawAddress: string): string {
    /**
     * for multiple addresses with semicolon, split and add 2 linebreaks after each
     */
    return rawAddress?.split(';').join(';\n\n');
  }

  chunkForCarousel(arr: Record<string, any>[], chunkSize = 3): any[][] {
    const chunks = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      chunks.push(arr.slice(i, i + chunkSize));
    }
    return chunks;
  }

  getFirstObjectKey(obj: Record<string, any>): string {
    return Object.keys(obj)[0];
  }

  isDate(val: string): boolean {
    if (!val) return;
    return val.toString().indexOf('-') >= 1;
  }

  openFullProfilePage(sectionId?: string): void {
    if (!this.companyNumberFromParent) return;
    window.open(
      '/profile/' + this.companyNumberFromParent + `${sectionId || ''}`,
      '_blank'
    );
  }

  getLandOwnershipData(): void {
    if (!this.featurePermissions.hasPAFAccess) return;

    this.updateEndpointsStateSignals(EndpointNames.LandOwnershipData, false);

    this.companyService
      .getLandRegistryAddresses(this.companyNumber)
      .subscribe((data: LandRegistryAddressPayload[]) => {
        if (Array.isArray(data) && data.length) {
          this.landOwnershipData = data.map(address => ({
            ...address,
            epc_data: Array.isArray(address?.epc_data)
              ? address.epc_data
              : [],
          }));
        } else {
          this.landOwnershipData = [];
        }
        this.expandedEpcCertificates = {};

        this.updateEndpointsStateSignals(EndpointNames.LandOwnershipData, true);
      });
  }

  hasEpcCertificates(addressData: LandRegistryAddressPayload): boolean {
    return (
      Array.isArray(addressData?.epc_data) && addressData.epc_data.length > 0
    );
  }

  trackEpcCertificate(
    index: number,
    certificate: LandRegistryEpcCertificate
  ): string {
    return this.getEpcCertificateId(certificate) ?? `${index}`;
  }

  getEpcCertificateId(
    certificate: LandRegistryEpcCertificate
  ): string | null {
    return (
      certificate?.certificate_identifier ||
      certificate?.certificate_number ||
      certificate?.legacy_lmk_key ||
      certificate?.lmk_key ||
      null
    );
  }

  getEpcCertificateAddress(
    certificate: LandRegistryEpcCertificate
  ): string | null {
    if (!certificate) return null;

    if (certificate.address) {
      return certificate.address;
    }

    const parts = [
      certificate.address_line_1,
      certificate.address_line_2,
      certificate.posttown,
      certificate.postcode,
    ].filter(Boolean) as string[];

    return parts.length ? parts.join(', ') : null;
  }

  toggleEpcCertificate(certificate: LandRegistryEpcCertificate): void {
    const key = this.getEpcCertificateId(certificate);
    if (!key) return;

    this.expandedEpcCertificates[key] = !this.expandedEpcCertificates[key];
  }

  isEpcCertificateExpanded(
    certificate: LandRegistryEpcCertificate
  ): boolean {
    const key = this.getEpcCertificateId(certificate);
    if (!key) return false;

    return !!this.expandedEpcCertificates[key];
  }

  ngOnDestroy(): void {
    this.stopAllPolling();
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }
}
